import { memo, useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import { isFunction, isNull } from 'lodash';
import { GooglePlaceAddress } from 'types/user';
import { useDidMount } from 'hooks/useDidMount';
import {
    AdvancedMarker,
    MapCameraChangedEvent,
    MapCameraProps,
    MapMouseEvent,
    useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { GoogleMap } from 'components/@shared/GoogleMap';
import { COMPANY_ADDRESS } from 'constants/general';

interface Props {
    placeId?: string;
    onChange?: (googleAddress: GooglePlaceAddress | null) => void;
}

export const GoogleAutocompleteMap = memo(({ placeId, onChange }: Props) => {
    const [marker, setMarker] = useState<google.maps.LatLngLiteral | null>(null);
    const [geocodeService, setSetGeocodeService] = useState<google.maps.Geocoder | null>(null);
    const [cameraProps, setCameraProps] = useState<MapCameraProps>({ center: COMPANY_ADDRESS, zoom: 15 });

    const geocoding = useMapsLibrary('geocoding');

    const handleCameraChange = useCallback((ev: MapCameraChangedEvent) => setCameraProps(ev.detail), []);

    const mapClickHandler = useCallback(
        (event: MapMouseEvent) => {
            if (isFunction(onChange) && !!geocodeService) {
                geocodeService.geocode({ location: event.detail.latLng }).then((result) => {
                    setMarker(event.detail.latLng);
                    onChange({ description: result.results[0].formatted_address, placeId: result.results[0].place_id });
                });
            }
        },
        [onChange, geocodeService],
    );

    useEffect(() => {
        if (!geocoding) return;
        setSetGeocodeService(new geocoding.Geocoder());
        return () => setSetGeocodeService(null);
    }, [geocoding]);

    useEffect(() => {
        if (!geocodeService) return;
        if (placeId) {
            geocodeService.geocode({ placeId }).then((result) => {
                const latLng = {
                    lat: result.results[0].geometry.location.lat(),
                    lng: result.results[0].geometry.location.lng(),
                };
                setMarker(latLng);
                setCameraProps((prevState) => ({ ...prevState, center: latLng }));
            });
        }
    }, [geocodeService, placeId]);

    return (
        <Box height="300px" pt={0.5}>
            <GoogleMap
                mapId="autocomplete-address"
                {...cameraProps}
                onClick={mapClickHandler}
                onCameraChanged={handleCameraChange}
            >
                {marker && <AdvancedMarker position={marker} />}
            </GoogleMap>
        </Box>
    );
});

GoogleAutocompleteMap.displayName = 'GoogleAutocompleteMap';
